/**
 * 库存分配模拟数据
 */

import { randomBetween, randomChoice } from '@/mock/mockData';
import dayjs from 'dayjs';
import { 
  AllocationType, 
  AllocationStatus, 
  StockAllocationItem, 
  StockAllocation 
} from '@/api/modules/stockAllocation';

// 转换snake_case到camelCase的工具函数
function toCamelCase<T>(obj: any): T {
  if (typeof obj !== 'object' || obj === null) return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(v => toCamelCase<any>(v)) as any;
  }
  
  const camelCaseObj: Record<string, any> = {};
  
  for (const key in obj) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    camelCaseObj[camelKey] = toCamelCase<any>(obj[key]);
  }
  
  return camelCaseObj as T;
}

// 下级客户
const subCustomers = [
  { id: 'cust-001', name: '北京体育用品批发公司', level: 2, contact: '张经理 (13800138001)' },
  { id: 'cust-002', name: '上海运动装备有限公司', level: 2, contact: '李总 (13800138002)' },
  { id: 'cust-003', name: '广州健身器材批发商', level: 2, contact: '王先生 (13800138003)' },
  { id: 'cust-004', name: '深圳市运动科技有限公司', level: 2, contact: '赵小姐 (13800138004)' },
  { id: 'cust-005', name: '杭州市康体设备有限公司', level: 2, contact: '陈经理 (13800138005)' },
  { id: 'cust-006', name: '成都体育用品经销商', level: 2, contact: '郑总 (13800138006)' },
  { id: 'cust-007', name: '武汉健康产业有限公司', level: 2, contact: '黄经理 (13800138007)' },
  { id: 'cust-008', name: '南京运动器材批发中心', level: 2, contact: '吴总 (13800138008)' }
];

// 仓库列表
const warehouses = [
  { id: 'wh-001', name: '华北中央仓库' },
  { id: 'wh-002', name: '华东配送中心' },
  { id: 'wh-003', name: '华南物流基地' },
  { id: 'wh-004', name: '西南区域仓' },
  { id: 'wh-005', name: '西北物流中心' }
];

// 生成分配项的商品
const generateAllocationProducts = () => {
  // 使用现有的商品名称
  const productNames = [
    '李宁韦德之道10代', '李宁韦德之道9代', '李宁驭帅15', '李宁空袭7',
    '李宁全城9', '李宁音速9', '李宁闪击7', '李宁风暴',
    '李宁超轻19代', '李宁云四代', '李宁赤兔6代', '李宁飞电3',
    '李宁绝影', '李宁风云', '李宁疾风', '李宁云马',
    '李宁突袭TD', '李宁雷霆', '李宁风暴战靴', '李宁风动'
  ];

  return productNames.map((name, index) => ({
    id: `prod-${String(index + 1).padStart(3, '0')}`,
    name,
    sku: `LN${String(index + 1).padStart(8, '0')}`,
    image: `https://picsum.photos/400/400?random=${index + 1}`,
    price: randomBetween(199, 1299),
    stock: randomBetween(100, 2000)
  }));
};

const products = generateAllocationProducts();

/**
 * 生成单个库存分配项
 */
const generateAllocationItem = (allocationId: string, index: number): StockAllocationItem => {
  const product = products[randomBetween(0, products.length - 1)];
  const allocationType = randomChoice(Object.values(AllocationType));
  
  let allocationValue, allocatedQuantity;
  if (allocationType === AllocationType.PERCENTAGE) {
    allocationValue = randomBetween(10, 80); // 10% - 80%
    allocatedQuantity = Math.floor(product.stock * (allocationValue / 100));
  } else if (allocationType === AllocationType.FIXED) {
    allocatedQuantity = randomBetween(10, 500);
    allocationValue = allocatedQuantity;
  } else {
    allocatedQuantity = product.stock;
    allocationValue = 100;
  }

  const soldQuantity = randomBetween(0, allocatedQuantity);
  const remainingQuantity = allocatedQuantity - soldQuantity;
  
  // 50% 的几率指定仓库
  const hasWarehouse = Math.random() > 0.5;
  const warehouse = hasWarehouse ? warehouses[randomBetween(0, warehouses.length - 1)] : null;

  return {
    id: `${allocationId}-item-${index + 1}`,
    productId: product.id,
    productName: product.name,
    productSku: product.sku,
    productImage: product.image,
    allocationType: allocationType,
    allocationValue: allocationValue,
    allocatedQuantity: allocatedQuantity,
    minOrderQuantity: randomBetween(1, 10),
    discount: randomBetween(70, 95), // 70% - 95% 的折扣
    remainingQuantity: remainingQuantity,
    soldQuantity: soldQuantity,
    warehouseId: warehouse?.id,
    warehouseName: warehouse?.name
  };
};

/**
 * 生成单个库存分配
 */
const generateAllocation = (index: number): StockAllocation => {
  const customer = subCustomers[randomBetween(0, subCustomers.length - 1)];
  const status = randomChoice(Object.values(AllocationStatus));
  
  // 创建一个过去的日期
  const createdAt = dayjs()
    .subtract(randomBetween(1, 100), 'day')
    .format('YYYY-MM-DD HH:mm:ss');
  
  // 更新日期在创建日期之后
  const updatedAt = dayjs(createdAt)
    .add(randomBetween(0, 30), 'day')
    .format('YYYY-MM-DD HH:mm:ss');

  // 有效期设置
  const effectiveDate = dayjs(createdAt)
    .format('YYYY-MM-DD');
  
  const expiryDate = dayjs(effectiveDate)
    .add(randomBetween(30, 365), 'day')
    .format('YYYY-MM-DD');

  const id = `alloc-${String(index + 1).padStart(6, '0')}`;
  const itemCount = randomBetween(1, 10);
  const items: StockAllocationItem[] = [];
  
  for (let i = 0; i < itemCount; i++) {
    items.push(generateAllocationItem(id, i));
  }

  const totalAllocatedQuantity = items.reduce((sum, item) => sum + item.allocatedQuantity, 0);
  const warehouseRule = Math.random() > 0.5 ? 'all' : 'specific';

  return {
    id,
    name: `${customer.name}库存分配方案${index + 1}`,
    customerId: customer.id,
    customerName: customer.name,
    customerLevel: customer.level,
    customerContact: customer.contact,
    status,
    effectiveDate: effectiveDate,
    expiryDate: expiryDate,
    createdAt: createdAt,
    updatedAt: updatedAt,
    createdBy: '系统管理员',
    updatedBy: '系统管理员',
    description: `为${customer.name}分配的库存资源，包含${itemCount}种商品，总计${totalAllocatedQuantity}件商品。`,
    warehouseRule,
    items,
    totalItems: itemCount,
    totalAllocatedQuantity: totalAllocatedQuantity
  };
};

/**
 * 生成库存分配列表
 */
export const generateAllocationList = (page: number, pageSize: number, filters: any = {}): Promise<{
  data: StockAllocation[];
  count: number;
  success: boolean;
}> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const totalCount = 100; // 总记录数
      const allAllocations: StockAllocation[] = [];
      
      // 生成所有分配数据
      for (let i = 0; i < totalCount; i++) {
        allAllocations.push(generateAllocation(i));
      }
      
      // 应用筛选条件
      let filteredAllocations = allAllocations;
      
      if (filters.name) {
        filteredAllocations = filteredAllocations.filter(allocation => 
          allocation.name.toLowerCase().includes(filters.name.toLowerCase())
        );
      }
      
      if (filters.customerName) {
        filteredAllocations = filteredAllocations.filter(allocation =>
          allocation.customerName.toLowerCase().includes(filters.customerName.toLowerCase())
        );
      }
      
      if (filters.status) {
        filteredAllocations = filteredAllocations.filter(allocation => 
          allocation.status === filters.status
        );
      }
      
      if (filters.effectiveDate) {
        const [startDate, endDate] = filters.effectiveDate;
        filteredAllocations = filteredAllocations.filter(allocation =>
          dayjs(allocation.effectiveDate).isAfter(startDate) &&
          dayjs(allocation.effectiveDate).isBefore(endDate)
        );
      }

      if (filters.expiryDate) {
        const [startDate, endDate] = filters.expiryDate;
        filteredAllocations = filteredAllocations.filter(allocation =>
          dayjs(allocation.expiryDate).isAfter(startDate) &&
          dayjs(allocation.expiryDate).isBefore(endDate)
        );
      }
      
      // 分页处理
      const start = (page - 1) * pageSize;
      const end = start + pageSize;
      const data = filteredAllocations.slice(start, end);
      
      resolve({
        data,
        count: filteredAllocations.length,
        success: true,
      });
    }, randomBetween(300, 800)); // 模拟网络延迟
  });
};

/**
 * 获取单个库存分配详情
 */
export const getAllocationDetail = (id: string): Promise<{
  data: StockAllocation | null;
  success: boolean;
}> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const allocationIndex = parseInt(id.replace('alloc-', '')) - 1;
      const allocation = allocationIndex >= 0 && allocationIndex < 100 ? generateAllocation(allocationIndex) : null;
      
      resolve({
        data: allocation,
        success: true,
      });
    }, randomBetween(200, 500));
  });
};

/**
 * 获取可分配的下级客户列表
 */
export const getSubCustomers = (): Promise<{
  data: Array<{ id: string; name: string; level: number; contact: string }>;
  count: number;
  success: boolean;
}> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        data: subCustomers,
        count: subCustomers.length,
        success: true,
      });
    }, randomBetween(100, 300));
  });
};

/**
 * 获取可分配的仓库列表
 */
export const getWarehouses = (): Promise<{
  data: Array<{ id: string; name: string }>;
  count: number;
  success: boolean;
}> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        data: warehouses,
        count: warehouses.length,
        success: true,
      });
    }, randomBetween(100, 300));
  });
};

/**
 * 获取可分配的商品
 */
export const getAllocatableProducts = (): Promise<{
  data: Array<any>;
  count: number;
  success: boolean;
}> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        data: products,
        count: products.length,
        success: true,
      });
    }, randomBetween(100, 300));
  });
};

/**
 * 获取分配历史记录
 */
export const getAllocationHistory = (allocationId: string): Promise<{
  data: Array<{
    id: string;
    allocation_id: string;
    action: 'create' | 'update' | 'delete' | 'status_change';
    status_before?: AllocationStatus;
    status_after?: AllocationStatus;
    changes?: {
      field: string;
      before: any;
      after: any;
    }[];
    created_at: string;
    created_by: string;
    user_name: string;
    description: string;
  }>;
  count: number;
  success: boolean;
}> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const historyCount = randomBetween(3, 10);
      const history = [];
      
      // 创建历史记录
      const baseDate = dayjs().subtract(randomBetween(10, 100), 'day');
      
      // 第一条永远是创建记录
      history.push({
        id: `hist-${allocationId}-1`,
        allocation_id: allocationId,
        action: 'create',
        created_at: baseDate.format('YYYY-MM-DD HH:mm:ss'),
        created_by: 'admin',
        user_name: '系统管理员',
        description: '创建了库存分配方案'
      });
      
      // 其余的随机操作
      const actions = ['update', 'status_change'] as Array<'update' | 'status_change'>;
      const actionTexts: Record<string, string> = {
        'update': '更新了库存分配信息',
        'status_change': '修改了分配状态'
      };
      
      for (let i = 2; i <= historyCount; i++) {
        const action = randomChoice(actions) as 'update' | 'status_change';
        const date = baseDate.add(i, 'day').format('YYYY-MM-DD HH:mm:ss');
        
        const historyItem: any = {
          id: `hist-${allocationId}-${i}`,
          allocation_id: allocationId,
          action,
          created_at: date,
          created_by: 'admin',
          user_name: '系统管理员',
          description: actionTexts[action]
        };
        
        // 如果是状态变更，添加前后状态
        if (action === 'status_change') {
          const allStatuses = Object.values(AllocationStatus);
          const statusBefore = randomChoice(allStatuses);
          let statusAfter;
          
          do {
            statusAfter = randomChoice(allStatuses);
          } while (statusAfter === statusBefore);
          
          historyItem.status_before = statusBefore;
          historyItem.status_after = statusAfter;
        }
        
        // 如果是更新，添加变更字段
        if (action === 'update') {
          historyItem.changes = [
            {
              field: '有效期',
              before: dayjs().format('YYYY-MM-DD'),
              after: dayjs().add(30, 'day').format('YYYY-MM-DD')
            }
          ];
        }
        
        history.push(historyItem);
      }
      
      resolve({
        data: history,
        count: history.length,
        success: true,
      });
    }, randomBetween(200, 500));
  });
};
